/**
 * useCredentials Hook
 * Credentials kezelése React komponensekben
 */

import { useState, useEffect, useCallback } from 'react';
import { Credential, CredentialType } from '../types/credentials';
import { 
  getCredentials, 
  getCredentialsByType, 
  getCredential,
  createCredential,
  updateCredential,
  deleteCredential
} from '../services/credentialsService';
import { useAuth } from '../contexts/AuthContext';

export function useCredentials(type?: CredentialType) {
  const { user } = useAuth();
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const organizationId = user?.organizationId || 'default-org';

  // Credentials betöltése
  const loadCredentials = useCallback(async () => {
    if (!organizationId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const data = type 
        ? await getCredentialsByType(organizationId, type)
        : await getCredentials(organizationId);
      
      setCredentials(data);
    } catch (err: any) {
      setError(err.message || 'Hiba a credentials betöltése során');
      console.error('Error loading credentials:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId, type]);

  // Kezdeti betöltés
  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  // Credential létrehozása
  const create = useCallback(async (
    credential: Omit<Credential, 'id' | 'createdAt' | 'updatedAt' | 'usageCount' | 'createdBy'> & { organizationId?: string }
  ): Promise<string> => {
    try {
      const id = await createCredential({
        ...credential,
        organizationId: credential.organizationId || organizationId, // Használja a megadottat vagy az aktuális user-ét
        createdBy: user?.uid || 'unknown'
      });
      
      await loadCredentials(); // Újratöltés
      return id;
    } catch (err: any) {
      setError(err.message || 'Hiba a credential létrehozása során');
      throw err;
    }
  }, [organizationId, user, loadCredentials]);

  // Credential frissítése
  const update = useCallback(async (
    credentialId: string,
    updates: Partial<Omit<Credential, 'id' | 'createdAt' | 'organizationId' | 'createdBy'>>
  ): Promise<void> => {
    try {
      await updateCredential(credentialId, updates);
      await loadCredentials(); // Újratöltés
    } catch (err: any) {
      setError(err.message || 'Hiba a credential frissítése során');
      throw err;
    }
  }, [loadCredentials]);

  // Credential törlése
  const remove = useCallback(async (credentialId: string): Promise<void> => {
    try {
      await deleteCredential(credentialId);
      await loadCredentials(); // Újratöltés
    } catch (err: any) {
      setError(err.message || 'Hiba a credential törlése során');
      throw err;
    }
  }, [loadCredentials]);

  // Credential lekérése ID alapján
  const getById = useCallback(async (credentialId: string): Promise<Credential | null> => {
    try {
      return await getCredential(credentialId);
    } catch (err: any) {
      setError(err.message || 'Hiba a credential lekérése során');
      throw err;
    }
  }, []);

  return {
    credentials,
    loading,
    error,
    create,
    update,
    remove,
    getById,
    refresh: loadCredentials
  };
}

// Egyszerűsített hook egy credential típushoz
export function useCredentialsByType(type: CredentialType) {
  return useCredentials(type);
}

// Hook egy konkrét credential lekérésére
export function useCredential(credentialId: string | null) {
  const [credential, setCredential] = useState<Credential | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!credentialId) {
      setCredential(null);
      setLoading(false);
      return;
    }

    const loadCredential = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getCredential(credentialId);
        setCredential(data);
      } catch (err: any) {
        setError(err.message || 'Hiba a credential betöltése során');
        console.error('Error loading credential:', err);
      } finally {
        setLoading(false);
      }
    };

    loadCredential();
  }, [credentialId]);

  return { credential, loading, error };
}
